import { useEffect, useState } from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';

export interface AvailableProgram {
  id: number;
  name: string;
}

interface SeatPool {
  program_id: number;
}

interface SerializedProgram {
  full_title_with_edition: string;
  id: number;
}

interface UseAvailableProgramsResult {
  availablePrograms: AvailableProgram[];
  error: string | null;
  isLoading: boolean;
}

const useAvailablePrograms = (subscriberId: string): UseAvailableProgramsResult => {
  const [availablePrograms, setAvailablePrograms] = useState<AvailableProgram[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    const seatPoolsRequest = Axios.get<{ data: SeatPool[] }>(
      Routes.plato_api_seat_pools_path({
        active: true,
        for_subscriber: subscriberId,
        included_in_rostering: true,
        non_welcome: true,
        only: ['program_id'],
        order: 'asc',
        per_page: 500,
        with_current_subscriptions: true,
      }),
    );

    const programsRequest = Axios.get<{ data: SerializedProgram[] }>(
      Routes.plato_api_subscriber_programs_path(subscriberId, {
        only: ['id', 'full_title_with_edition'],
        per_page: 500,
      }),
    );

    Promise.all([seatPoolsRequest, programsRequest])
      .then(([seatPoolsRes, programsRes]) => {
        const seatPools = seatPoolsRes.data.data || [];
        const validProgramIds = new Set(seatPools.map(sp => sp.program_id));

        const programs = programsRes.data.data || [];
        const filtered = programs
          .filter(p => validProgramIds.has(p.id))
          .map(p => ({ id: p.id, name: p.full_title_with_edition }));

        setAvailablePrograms(filtered);
      })
      .catch(() => {
        setAvailablePrograms([]);
        setError('Failed to load available programs');
      })
      .finally(() => setIsLoading(false));
  }, [subscriberId]);

  return { availablePrograms, error, isLoading };
};

export default useAvailablePrograms;
